import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Mail, MessageCircle, Send, Clock, Loader2 } from "lucide-react";
import { COMPANY } from "../lib/constants";

// Endpoint do formulário: usa PHP estático se REACT_APP_CONTACT_ENDPOINT estiver definido
// (build para hospedagem compartilhada), senão usa o backend FastAPI (preview/dev).
const CONTACT_ENDPOINT =
  process.env.REACT_APP_CONTACT_ENDPOINT ||
  `${process.env.REACT_APP_BACKEND_URL}/api/contact`;

const INITIAL = { nome: "", empresa: "", telefone: "", email: "", mensagem: "" };

export default function Contact() {
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    if (form.nome.trim().length < 2) return toast.error("Informe seu nome completo.");
    if (form.telefone.trim().length < 8) return toast.error("Informe um telefone válido.");
    if (!/^\S+@\S+\.\S+$/.test(form.email)) return toast.error("Informe um e-mail válido.");
    if (form.mensagem.trim().length < 5) return toast.error("Escreva uma mensagem.");

    setSubmitting(true);
    try {
      const res = await axios.post(CONTACT_ENDPOINT, form);
      // PHP retorna { ok: true } ou { error: '...' }
      if (res.data && res.data.error) throw new Error(res.data.error);
      toast.success("Mensagem enviada com sucesso! Entraremos em contato em breve.");
      setForm(INITIAL);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Erro ao enviar. Tente novamente em instantes.";
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    "w-full bg-white border border-gray-200 rounded-xl px-4 py-3.5 text-[#111111] placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#00C7A5] focus:border-transparent transition-all";

  return (
    <section id="contatos" data-testid="contact-section" className="py-20 md:py-32 bg-white relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-14 reveal">
          <p className="text-sm font-bold uppercase tracking-[0.25em] text-[#00C7A5] mb-4">Contato</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#2F5887] leading-tight mb-5">
            Vamos conversar sobre o seu negócio?
          </h2>
          <p className="text-[#4B5563] text-lg leading-relaxed">
            Preencha o formulário ou utilize um de nossos canais de atendimento. Nosso time responde rapidamente.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12">
          {/* Form */}
          <div className="lg:col-span-7 reveal">
            <form
              data-testid="contact-form"
              onSubmit={handleSubmit}
              className="bg-[#F5F7FA] rounded-2xl p-6 md:p-10 border border-gray-100 shadow-sm"
              noValidate
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="nome" className="block text-sm font-semibold text-[#111111] mb-2">
                    Nome completo *
                  </label>
                  <input
                    data-testid="contact-input-nome"
                    type="text"
                    id="nome"
                    name="nome"
                    value={form.nome}
                    onChange={handleChange}
                    placeholder="Seu nome"
                    className={inputClass}
                    required
                  />
                </div>
                <div>
                  <label htmlFor="empresa" className="block text-sm font-semibold text-[#111111] mb-2">
                    Empresa
                  </label>
                  <input
                    data-testid="contact-input-empresa"
                    type="text"
                    id="empresa"
                    name="empresa"
                    value={form.empresa}
                    onChange={handleChange}
                    placeholder="Nome da empresa"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label htmlFor="telefone" className="block text-sm font-semibold text-[#111111] mb-2">
                    Telefone *
                  </label>
                  <input
                    data-testid="contact-input-telefone"
                    type="tel"
                    id="telefone"
                    name="telefone"
                    value={form.telefone}
                    onChange={handleChange}
                    placeholder="(00) 00000-0000"
                    className={inputClass}
                    required
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-[#111111] mb-2">
                    E-mail *
                  </label>
                  <input
                    data-testid="contact-input-email"
                    type="email"
                    id="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="voce@empresa.com.br"
                    className={inputClass}
                    required
                  />
                </div>
              </div>

              <div className="mt-5">
                <label htmlFor="mensagem" className="block text-sm font-semibold text-[#111111] mb-2">
                  Mensagem *
                </label>
                <textarea
                  data-testid="contact-input-mensagem"
                  id="mensagem"
                  name="mensagem"
                  rows={5}
                  value={form.mensagem}
                  onChange={handleChange}
                  placeholder="Conte-nos um pouco sobre a sua necessidade..."
                  className={inputClass + " resize-none"}
                  required
                />
              </div>

              <button
                data-testid="contact-submit-button"
                type="submit"
                disabled={submitting}
                className="mt-6 w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#00C7A5] hover:bg-[#00A88A] disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold uppercase tracking-wide px-8 py-4 rounded-full transition-all hover:-translate-y-0.5 shadow-[0_10px_25px_rgba(0,199,165,0.35)]"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Enviar mensagem
                  </>
                )}
              </button>

              <p className="text-xs text-[#4B5563] mt-4">
                Seus dados são tratados com total confidencialidade.
              </p>
            </form>
          </div>

          {/* Info side */}
          <div className="lg:col-span-5 reveal space-y-5">
            <InfoCard
              icon={MapPin}
              title="Endereço"
              lines={[
                COMPANY.address.street,
                `${COMPANY.address.neighborhood} — ${COMPANY.address.city}`,
                COMPANY.address.zip,
              ]}
              testid="info-address"
            />
            <InfoCard
              icon={Phone}
              title="Telefone"
              lines={[COMPANY.phone]}
              href={`tel:${COMPANY.phoneRaw}`}
              testid="info-phone"
            />
            <InfoCard
              icon={Mail}
              title="E-mail"
              lines={[COMPANY.email]}
              href={`mailto:${COMPANY.email}`}
              testid="info-email"
            />
            <InfoCard
              icon={Clock}
              title="Atendimento"
              lines={["Segunda a Sexta", "08h às 18h"]}
              testid="info-hours"
            />

            <a
              data-testid="contact-whatsapp-button"
              href={COMPANY.whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1da851] text-white font-semibold uppercase tracking-wide px-6 py-4 rounded-2xl transition-all hover:-translate-y-0.5 shadow-md"
            >
              <MessageCircle className="w-5 h-5" />
              Conversar no WhatsApp
            </a>
          </div>
        </div>

        {/* Map */}
        <div className="reveal mt-12 lg:mt-16 rounded-2xl overflow-hidden shadow-lg border border-gray-100">
          <iframe
            data-testid="contact-map"
            title="Mapa Master Business"
            src={COMPANY.mapsEmbed}
            width="100%"
            height="420"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      </div>
    </section>
  );
}

function InfoCard({ icon: Icon, title, lines, href, testid }) {
  const content = (
    <div
      data-testid={testid}
      className="bg-white border border-gray-100 rounded-2xl p-6 flex items-start gap-4 hover:shadow-lg hover:-translate-y-0.5 transition-all"
    >
      <div className="w-12 h-12 rounded-xl bg-[#00C7A5]/10 flex items-center justify-center flex-shrink-0">
        <Icon className="w-6 h-6 text-[#00C7A5]" />
      </div>
      <div>
        <p className="text-xs font-bold uppercase tracking-widest text-[#4B5563] mb-1">{title}</p>
        {lines.map((l, i) => (
          <p key={i} className="text-[#111111] font-medium leading-snug">
            {l}
          </p>
        ))}
      </div>
    </div>
  );

  if (href) {
    return (
      <a href={href} className="block">
        {content}
      </a>
    );
  }
  return content;
}
